import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Search,
  MessageCircle,
  Sparkles,
  Star,
  Calendar,
  AlertOctagon,
  Handshake,
  Eye,
  RefreshCw,
  Copy,
} from "lucide-react";
import { format, differenceInCalendarDays, differenceInCalendarMonths, startOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { calculateClientScore } from "@/lib/clientScore";
import { toast } from "sonner";
import { useCurrency } from "@/hooks/useUserCurrency";

const safeFormat = (d: Date | null | undefined, fmt: string) => {
  try {
    if (!(d instanceof Date) || isNaN(d.getTime())) return "—";
    return format(d, fmt, { locale: ptBR });
  } catch {
    return "—";
  }
};

type Loan = {
  id: string;
  clientId: string;
  clientName: string;
  phone: string | null;
  amount: number;
  interestRate: number;
  interestType: "monthly" | "fixed" | "daily";
  status: "paid" | "active" | "overdue";
  dueDate: Date;
  loanDate: Date;
  updatedAt: Date;
};

type Payment = {
  loanId: string;
  paidAt: Date;
  interestAmount: number;
  principalAmount: number;
  amount: number;
};

interface Props {
  loans: Loan[];
  payments: Payment[];
}

const STORAGE_KEY = "intel_collection_state_v1";
type State = Record<string, "negotiated" | undefined>;

function loadState(): State {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}
function saveState(s: State) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
}

function urgencyOf(days: number): { label: string; tone: "warning" | "destructive"; bar: string } {
  if (days > 30) return { label: "Crítica", tone: "destructive", bar: "bg-destructive" };
  if (days >= 8) return { label: "Alta", tone: "destructive", bar: "bg-destructive" };
  if (days >= 1) return { label: "Moderada", tone: "warning", bar: "bg-gold" };
  return { label: "Hoje", tone: "warning", bar: "bg-gold" };
}

export function CollectionTab({ loans, payments }: Props) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"open" | "negotiated" | "all">("open");
  const [state, setState] = useState<State>(loadState());
  const [details, setDetails] = useState<any | null>(null);
  const [msg, setMsg] = useState<{
    name: string;
    phone: string | null;
    text: string;
    days: number;
    open: number;
  } | null>(null);

  const today = startOfDay(new Date());

  const scoreLoans = loans.map((l) => ({
    id: l.id,
    clientId: l.clientId,
    amount: l.amount,
    dueDate: l.dueDate,
    loanDate: l.loanDate,
    status: l.status,
    interestType: l.interestType,
  }));
  const scorePayments = payments.map((p) => ({
    loanId: p.loanId,
    paidAt: p.paidAt,
    interestAmount: p.interestAmount,
  }));

  const items = useMemo(() => {
    return loans
      .filter(
        (l) =>
          l.dueDate instanceof Date &&
          !isNaN(l.dueDate.getTime()) &&
          (l.status === "overdue" || (l.status === "active" && l.dueDate < today))
      )
      .map((l) => {
        try {
          const days = differenceInCalendarDays(today, l.dueDate);
          const lateFee = days * 10;
          const overdueMonths = (() => {
            const diff = differenceInCalendarMonths(today, l.dueDate);
            return today.getDate() >= l.dueDate.getDate() ? diff + 1 : diff;
          })();
          const rate = Number(l.interestRate) || 0;
          const amount = Number(l.amount) || 0;
          const interest =
            l.interestType === "monthly"
              ? (amount * rate) / 100 * Math.max(overdueMonths, 1)
              : 0;
          const open = amount + interest + (days > 0 ? lateFee : 0);
          const score = calculateClientScore(l.clientId, scoreLoans, scorePayments);
          const u = urgencyOf(days);
          return { loan: l, days, open, score, urgency: u };
        } catch (e) {
          console.error("CollectionTab item error", l?.id, e);
          return null;
        }
      })
      .filter((x): x is NonNullable<typeof x> => x !== null)
      .sort((a, b) => b.days - a.days);
  }, [loans, payments]);

  const filtered = items.filter((it) => {
    const s = state[it.loan.id];
    if (filter === "open" && s === "negotiated") return false;
    if (filter === "negotiated" && s !== "negotiated") return false;
    if (search && !it.loan.clientName.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const setStatus = (loanId: string, value: "negotiated" | undefined) => {
    const next = { ...state };
    if (value === undefined) delete next[loanId];
    else next[loanId] = value;
    setState(next);
    saveState(next);
  };

  const pick = <T,>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)];

  const { format } = useCurrency();

  const generateMessage = (name: string, days: number, open: number) => {
    const first = name.split(" ")[0];
    const valor = format(open);

    // Vence hoje — cordial
    if (days <= 0) {
      return pick([
        `Olá ${first}, tudo bem? Passando para lembrar que sua parcela vence hoje, no valor de ${valor}. Posso te ajudar com o pagamento?`,
        `Oi ${first}! Só um lembrete rápido: sua parcela de ${valor} vence hoje. Me avisa por aqui assim que conseguir efetuar.`,
        `${first}, tudo certo? Sua parcela de ${valor} está prevista para hoje. Qualquer dúvida, é só me chamar.`,
      ]);
    }

    // 1 a 3 dias — cordial e educado
    if (days <= 3) {
      return pick([
        `Olá ${first}, identificamos que sua parcela está pendente há ${days} dia(s), no valor de ${valor}. Me chama aqui para regularizarmos, por favor.`,
        `Oi ${first}, tudo bem? Notamos que seu pagamento de ${valor} ainda não foi identificado (${days} dia(s) em atraso). Consegue resolver hoje?`,
        `${first}, passando para avisar que sua parcela de ${valor} consta em aberto há ${days} dia(s). Me retorna para combinarmos o pagamento.`,
      ]);
    }

    // 4 a 9 dias — firme e profissional
    if (days <= 9) {
      return pick([
        `Olá ${first}, seu pagamento de ${valor} segue pendente há ${days} dias. Preciso do seu retorno hoje para regularização.`,
        `${first}, sua parcela de ${valor} está em aberto há ${days} dias. Solicito que entre em contato hoje para alinharmos o pagamento.`,
        `Olá ${first}, registro que sua pendência de ${valor} já soma ${days} dias de atraso. Aguardo seu retorno ainda hoje para regularizarmos.`,
      ]);
    }

    // 10 a 29 dias — sério, direto, profissional
    if (days < 30) {
      return pick([
        `${first}, seu débito de ${valor} permanece em aberto há ${days} dias. É necessário resolver essa pendência com urgência. Aguardo seu retorno imediato.`,
        `${first}, registro que sua dívida de ${valor} está em atraso há ${days} dias. Solicito posicionamento hoje para evitarmos o agravamento da situação.`,
        `${first}, sua pendência de ${valor} ultrapassou ${days} dias em aberto. Preciso de uma definição sua hoje para tratarmos a regularização.`,
      ]);
    }

    // 30+ dias — crítico e altamente objetivo
    return pick([
      `${first}, sua pendência de ${valor} já ultrapassa ${days} dias em aberto. Precisamos tratar essa situação imediatamente. Entre em contato hoje para definição.`,
      `${first}, seu débito de ${valor} está em atraso há ${days} dias. Esta é uma pendência crítica e exige resolução imediata. Aguardo seu contato ainda hoje.`,
      `${first}, sua dívida de ${valor} permanece em aberto há mais de ${days} dias. Solicito retorno imediato para definirmos o encaminhamento desta pendência.`,
    ]);
  };

  const sendWhatsApp = (phone: string | null, text: string) => {
    if (!phone) {
      toast.error("Cliente sem telefone cadastrado");
      return;
    }
    const digits = phone.replace(/\D/g, "");
    window.open(`https://wa.me/55${digits}?text=${encodeURIComponent(text)}`, "_blank");
  };

  return (
    <div className="space-y-5">
      <div className="glass rounded-2xl p-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar cliente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{filtered.length} caso(s)</span>
          <Select value={filter} onValueChange={(v: any) => setFilter(v)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Em aberto</SelectItem>
              <SelectItem value="negotiated">Negociados</SelectItem>
              <SelectItem value="all">Todos</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="glass p-10 rounded-2xl text-center">
          <AlertOctagon className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Nenhum caso de cobrança no momento.</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((it) => {
            const negotiated = state[it.loan.id] === "negotiated";
            const toneMap: Record<string, string> = {
              success: "text-success",
              warning: "text-gold",
              destructive: "text-destructive",
            };
            return (
              <Card key={it.loan.id} className="relative overflow-hidden p-5">
                <div
                  className={`absolute inset-x-0 top-0 h-px bg-gradient-to-r ${
                    it.urgency.tone === "destructive"
                      ? "from-destructive via-destructive/60 to-transparent"
                      : "from-gold via-gold/60 to-transparent"
                  }`}
                />
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold tracking-tight truncate">
                      {it.loan.clientName}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Star className={`h-3 w-3 ${toneMap[it.score.tone]}`} />
                      <span className={`text-xs font-medium ${toneMap[it.score.tone]}`}>
                        {it.score.label} · {it.score.score}
                      </span>
                    </div>
                  </div>
                  <Badge
                    variant={it.urgency.tone === "destructive" ? "destructive" : "default"}
                    className="shrink-0"
                  >
                    {it.urgency.label}
                  </Badge>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> Atraso
                    </span>
                    <span className="tabular font-medium">
                      {it.days <= 0 ? "Vence hoje" : `${it.days} dia(s)`}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Valor em aberto</span>
                    <span className="tabular font-semibold text-destructive">
                      {format(it.open)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Vencimento</span>
                    <span className="tabular">
                      {safeFormat(it.loan.dueDate, "dd/MM/yyyy")}
                    </span>
                  </div>
                  {negotiated && (
                    <Badge variant="secondary" className="mt-1">Negociado</Badge>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setMsg({
                        name: it.loan.clientName,
                        phone: it.loan.phone,
                        text: generateMessage(it.loan.clientName, it.days, it.open),
                        days: it.days,
                        open: it.open,
                      })
                    }
                  >
                    <Sparkles className="h-3.5 w-3.5 mr-1" /> Mensagem
                  </Button>
                  <Button
                    size="sm"
                    onClick={() =>
                      sendWhatsApp(
                        it.loan.phone,
                        generateMessage(it.loan.clientName, it.days, it.open)
                      )
                    }
                  >
                    <MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp
                  </Button>
                  {!negotiated ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setStatus(it.loan.id, "negotiated")}
                    >
                      <Handshake className="h-3.5 w-3.5 mr-1" /> Negociado
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setStatus(it.loan.id, undefined)}
                    >
                      Reabrir
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setDetails(it)}>
                    <Eye className="h-3.5 w-3.5 mr-1" /> Detalhes
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Detalhes */}
      <Dialog open={!!details} onOpenChange={(o) => !o && setDetails(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Detalhes do contrato</DialogTitle>
            <DialogDescription>{details?.loan.clientName}</DialogDescription>
          </DialogHeader>
          {details && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Valor original</span><span className="tabular">{format(Number(details.loan.amount))}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Tipo</span><span>{details.loan.interestType === "monthly" ? "Padrão (mensal)" : "Valor fixo"}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Taxa</span><span className="tabular">{details.loan.interestRate}%</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Vencimento</span><span className="tabular">{safeFormat(details.loan.dueDate, "dd/MM/yyyy")}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Atraso</span><span className="tabular">{details.days} dia(s)</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Multa por atraso</span><span className="tabular">{format(details.days * 10)}</span></div>
              <div className="flex justify-between border-t border-border pt-2 mt-2"><span className="font-medium">Total em aberto</span><span className="tabular font-semibold text-destructive">{format(details.open)}</span></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetails(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mensagem */}
      <Dialog open={!!msg} onOpenChange={(o) => !o && setMsg(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mensagem para {msg?.name}</DialogTitle>
            <DialogDescription>
              {msg && (msg.days <= 0
                ? "Tom cordial · vence hoje"
                : msg.days <= 3
                ? "Tom cordial e educado · até 3 dias"
                : msg.days <= 9
                ? "Tom firme e profissional · 4 a 9 dias"
                : msg.days < 30
                ? "Tom sério e direto · 10+ dias"
                : "Tom crítico e objetivo · 30+ dias")}
            </DialogDescription>
          </DialogHeader>
          <textarea
            className="w-full min-h-[160px] rounded-md bg-card border border-border p-3 text-sm"
            value={msg?.text || ""}
            onChange={(e) => msg && setMsg({ ...msg, text: e.target.value })}
          />
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                if (!msg) return;
                let next = msg.text;
                // try a few times to avoid identical regeneration
                for (let i = 0; i < 6 && next === msg.text; i++) {
                  next = generateMessage(msg.name, msg.days, msg.open);
                }
                setMsg({ ...msg, text: next });
              }}
            >
              <RefreshCw className="h-4 w-4 mr-1" /> Refazer
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (msg) {
                  navigator.clipboard.writeText(msg.text);
                  toast.success("Mensagem copiada");
                }
              }}
            >
              <Copy className="h-4 w-4 mr-1" /> Copiar
            </Button>
            <Button
              onClick={() => {
                if (msg?.phone) {
                  const digits = msg.phone.replace(/\D/g, "");
                  window.open(
                    `https://wa.me/55${digits}?text=${encodeURIComponent(msg.text)}`,
                    "_blank"
                  );
                  setMsg(null);
                } else {
                  toast.error("Sem telefone cadastrado");
                }
              }}
            >
              <MessageCircle className="h-4 w-4 mr-1" /> Enviar WhatsApp
            </Button>
            <Button variant="ghost" onClick={() => setMsg(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}